<script setup lang="ts">
import type { Task } from '~/types/task.type'
import { Draggable } from '@fullcalendar/interaction/index.js'
import { timeEntryCloneEventId } from '~/types/calendar.type'

const { data: tasksData, execute: executeRecentTasksFetch, status } = useApiTaskGetListPaginated({
  perPage: 5,
  withProject: true,
  withTaskList: true,
}, {
  immediate: false,
  watch: false,
})

const recentTasks = useRecentTasks()

const recentTasksName = Symbol('recent-tasks')

const expandedNames = computed(() => {
  return recentTasks.value.expanded ? [recentTasksName.toString()] : []
})

function updateExpandedState(names: string[]) {
  recentTasks.value.expanded = names.includes(recentTasksName.toString())
  if (recentTasks.value.expanded)
    fetchRecentTasks()
}

async function fetchRecentTasks() {
  await executeRecentTasksFetch()
  if (tasksData.value)
    recentTasks.value.tasks = tasksData.value.data
}

if (recentTasks.value.expanded)
  await fetchRecentTasks()

const { isDragging, draggedTask } = storeToRefs(useDragAndDropStore())

const dragContainer = useTemplateRef('draggable')

const draggable = ref<Draggable>()

const lastManualTimeEntry = useLastManualTimeEntry()

function setLatestTimeEntryForSilhouette(task: Task<'withTaskList'>) {
  lastManualTimeEntry.value = {
    projectId: task.taskList.projectId,
    taskId: task.id,
    durationSec: 54000,
  }
}

watch(dragContainer, (element) => {
  if (!element)
    return
  initDraggable()
})

function initDraggable() {
  if (!dragContainer.value)
    return
  draggable.value = new Draggable(dragContainer.value, {
    itemSelector: '.draggable-task',
    eventData(eventEl) {
      if (!eventEl.getAttribute('data-task'))
        return
      const task: Task<'withTaskList'> = JSON.parse(eventEl.getAttribute('data-task')!) as Task<'withTaskList'>
      setLatestTimeEntryForSilhouette(task)
      return {
        title: 'Klon',
        id: timeEntryCloneEventId.toString(),
        duration: 900000, // 15 minutes
      }
    },
  })
  draggable.value.dragging.emitter.on('dragstart', () => isDragging.value = true)
  draggable.value.dragging.emitter.on('dragend', () => {
    isDragging.value = false
    draggedTask.value = undefined
  })
}
</script>

<template>
  <NCollapse class="!mx-0 pl-4 mb-4" :expanded-names="expandedNames" @update:expanded-names="updateExpandedState">
    <NCollapseItem :name="recentTasksName.toString()">
      <template #header>
        <div class="text-xs text-gray-6">
          Letzte 5 Aufgaben
        </div>
      </template>
      <NSpin :show="status === 'pending'">
        <div ref="draggable" class="space-y-2 -ml-4">
          <TimeTrackerTaskQuickStartButton v-for="task in recentTasks.tasks" :key="task.id" :task="task" :data-task="JSON.stringify(task)" />
        </div>
      </NSpin>
    </NCollapseItem>
  </NCollapse>
</template>
